import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { shouldHideDuration } from "../utils/sessionValidation";
import PlatformIcon from "../../../common/components/Icon/PlatformIcon/PlatformIcon";
import { FloatingTooltip } from "../../../common/components/Tooltip/FloatingTooltip";
import {
  ChartBarIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronRightIcon,
  ClockIcon,
  ExclamationCircleIcon,
  EyeIcon,
  UserIcon,
} from "@heroicons/react/outline";

export interface SessionCardProps {
  id: number;
  date: Date;
  score: number | null;
  maxScore: number | null;
  duration: number | null;
  progressionPercent?: number | null;
  isCompleted: boolean;
  isSucceeded: boolean;
  userName?: string;
  userAvatarUrl?: string;
  onClick?: () => void;
  className?: string;
  averageScore?: number | null;
  hasGazeData?: boolean;
  targetPlatform?: string | null;
  deviceModel?: string | null;
}

const SessionCard = ({
  date,
  score,
  maxScore,
  duration,
  progressionPercent,
  isCompleted,
  isSucceeded,
  userName,
  userAvatarUrl,
  onClick,
  className = "",
  averageScore,
  hasGazeData,
  targetPlatform,
  deviceModel,
}: SessionCardProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "fr" ? fr : enUS;

  const getTrend = (): number | null => {
    if (score == null || averageScore == null) return null;
    return score - averageScore;
  };
  const trend = getTrend();

  const formatDuration = (seconds: number | null): string => {
    if (seconds == null) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getScoreColor = (score: number | null, maxScore: number | null): string => {
    if (score == null || maxScore == null || maxScore === 0) return "text-gray-600";
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return "text-green-600";
    if (percentage >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score: number | null, maxScore: number | null): string => {
    if (score == null || maxScore == null || maxScore === 0) return "bg-gray-100";
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return "bg-green-100";
    if (percentage >= 50) return "bg-yellow-100";
    return "bg-red-100";
  };

  const getProgressionColor = (percent: number | null): string => {
    if (percent == null) return "text-gray-600";
    if (percent >= 80) return "text-green-600";
    if (percent >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const getCardStyles = () => {
    if (isCompleted) {
      return {
        border: "border-l-4 border-l-green-500 border-gray-200",
        bg: "bg-green-50/30",
      };
    }
    return {
      border: "border-l-4 border-l-orange-400 border-gray-200",
      bg: "bg-orange-50/20",
    };
  };

  const cardStyles = getCardStyles();
  const trendLabel =
    trend === null
      ? ""
      : trend > 0
      ? t("statsNew.session.aboveAverage")
      : trend < 0
      ? t("statsNew.session.belowAverage")
      : t("statsNew.session.atAverage");

  return (
    <div
      onClick={onClick}
      className={`${cardStyles.bg} rounded-xl border ${
        cardStyles.border
      } p-4 shadow-sm transition-all duration-150 ${
        onClick ? "cursor-pointer hover:shadow-md hover:border-green-300" : ""
      } ${className}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
            {userAvatarUrl ? (
              <img src={userAvatarUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <UserIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>

          <div className="text-sm">
            <p className="font-medium text-gray-900">
              {format(date, "dd MMM yyyy", { locale })} - {format(date, "HH:mm", { locale })}
            </p>
            <p className="text-gray-500">{userName || t("statsNew.sessionDetail.anonymous")}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isCompleted ? (
            <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
              <CheckIcon className="h-3.5 w-3.5" />
              {t("statsNew.session.completed")}
            </span>
          ) : (
            <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
              <ExclamationCircleIcon className="h-3.5 w-3.5" />
              {t("statsNew.session.incomplete")}
            </span>
          )}

          {isSucceeded && (
            <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
              <CheckCircleIcon className="h-3.5 w-3.5" />
              {t("statsNew.session.succeeded")}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg ${getScoreBgColor(
            score,
            maxScore,
          )}`}
        >
          <span className={`text-lg font-bold ${getScoreColor(score, maxScore)}`}>
            {score != null ? score.toFixed(1) : "N/A"}
          </span>
          {maxScore != null && <span className="text-sm text-gray-500">/ {maxScore}</span>}
          {trend !== null && (
            <FloatingTooltip content={trendLabel} ariaHidden>
              <span
                role="img"
                aria-label={trendLabel}
                tabIndex={0}
                className={`ml-1 text-sm font-bold ${
                  trend > 0 ? "text-green-600" : trend < 0 ? "text-red-500" : "text-gray-400"
                }`}
              >
                {trend > 0 ? "↑" : trend < 0 ? "↓" : "−"}
              </span>
            </FloatingTooltip>
          )}
        </div>

        {progressionPercent != null && (
          <div className="flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-gray-400" />
            <span className={`text-sm font-medium ${getProgressionColor(progressionPercent)}`}>
              {Math.round(progressionPercent)}%
            </span>
          </div>
        )}

        <div className="flex items-center gap-2 text-gray-600">
          <ClockIcon className="h-5 w-5" />
          <span className="text-sm font-medium">
            {shouldHideDuration(date.toISOString(), duration) ? "-" : formatDuration(duration)}
          </span>
        </div>

        <div className="relative group flex items-center">
          <PlatformIcon
            platform={targetPlatform || "Unknown"}
            iconProps={{ width: "20px", height: "20px", fill: "#6b7280" }}
          />
          <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
            {targetPlatform
              ? t(`statsNew.session.platform.${targetPlatform}`, { defaultValue: targetPlatform })
              : t("statsNew.session.platform.Unknown")}
            {deviceModel && <span className="block text-gray-300">{deviceModel}</span>}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
              <div className="w-2 h-2 bg-gray-800 rotate-45" />
            </div>
          </div>
        </div>

        {hasGazeData && (
          <div className="relative group flex items-center text-purple-600">
            <EyeIcon className="h-5 w-5 cursor-help" />
            <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              {t("statsNew.gazeHeatmap.dataAvailable")}
              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
                <div className="w-2 h-2 bg-gray-800 rotate-45" />
              </div>
            </div>
          </div>
        )}

        {onClick && (
          <div className="ml-auto">
            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionCard;
